import { motion } from "framer-motion";
import { AlertTriangle, Phone, MessageCircle, Check } from "lucide-react";

const Urgencias = () => {
  const symptoms = [
    "Dor de dentes persistente ou aguda",
    "Dente partido ou lascado",
    "Gengivas inflamadas ou a sangrar",
    "Perda de coroa ou restauração",
    "Abcesso dentário",
    "Trauma dentário (queda, acidente)",
  ];

  return (
    <section
      id="urgencias"
      className="py-16 md:py-24 bg-[#1A1A1A] text-white relative overflow-hidden"
      data-testid="urgencias-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 mb-6">
              <AlertTriangle className="w-4 h-4 text-white" />
              <span className="text-sm font-semibold text-white/90">
                Atendimento de Urgência
              </span>
            </div>

            <h2
              className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight mb-6"
              data-testid="urgencias-title"
            >
              Emergência Dentária?{" "}
              <span className="text-gray-400">Estamos Cá Para Si</span>
            </h2>

            <p className="text-lg text-white/80 leading-relaxed mb-8">
              Tem uma dor de dentes aguda ou sofreu um trauma? Atendemos
              emergências em Viseu de segunda a sexta, das 10h às 19h. Não deixe
              a dor esperar.
            </p>

            {/* Symptoms List */}
            <div className="mb-8">
              <h3 className="font-outfit text-xl font-medium mb-4">
                Quando nos contactar:
              </h3>
              <ul className="space-y-3">
                {symptoms.map((symptom, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3"
                    data-testid={`symptom-${index}`}
                  >
                    <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-[#1A1A1A]" />
                    </div>
                    <span className="text-white/90">{symptom}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Right Column - CTAs */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-10"
          >
            <h3 className="font-outfit text-2xl font-semibold mb-6 text-center">
              Contacte-nos Agora
            </h3>

            <div className="space-y-4">
              {/* Phone CTA */}
              <a
                href="tel:927525885"
                className="flex items-center justify-center gap-3 w-full bg-white text-[#1A1A1A] rounded-full px-8 py-4 font-bold hover:bg-gray-100 transition-all shadow-lg text-lg"
                data-testid="urgencia-phone-btn"
              >
                <Phone className="w-6 h-6 text-[#1A1A1A]" />
                <span className="text-[#1A1A1A]">Ligar: 927 525 885</span>
              </a>

              {/* WhatsApp CTA */}
              <a
                href="https://wa.me/351927525885?text=Olá,%20preciso%20de%20atendimento%20de%20urgência"
                target="_blank"
                rel="noopener noreferrer"
                className="whatsapp-btn flex items-center justify-center gap-3 w-full bg-[#4A4A4A] text-white rounded-full px-8 py-4 font-bold hover:bg-[#333333] transition-all shadow-lg text-lg"
                data-testid="whatsapp-btn"
              >
                <MessageCircle className="w-6 h-6 text-white" />
                <span className="text-white">Enviar WhatsApp</span>
              </a>
            </div>

            <p className="text-center text-white/60 text-sm mt-6">
              Envie uma foto ou descrição do problema via WhatsApp para uma
              avaliação mais rápida.
            </p>

            {/* Info */}
            <div className="mt-8 pt-6 border-t border-white/10">
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <span className="text-2xl font-bold text-white">10h-19h</span>
                  <p className="text-sm text-white/60 mt-1">Horário de Urgências</p>
                </div>
                <div>
                  <span className="text-2xl font-bold text-white">Seg-Sex</span>
                  <p className="text-sm text-white/60 mt-1">Dias Úteis</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Urgencias;
